/**
 * CurrencyService — Interface avec le backend pour la conversion de devises.
 *
 * Toutes les conversions passent par le backend pour garantir la cohérence
 * avec les taux stockés en base (source Frankfurter.app / BCE).
 * Cache côté frontend de 5 min pour éviter les appels répétés.
 */
import axios from 'axios';
import { ccySym } from '@/utils/currencyUtils';
import { BACKEND_URL } from '@/config';

const API = `${(BACKEND_URL || 'http://localhost:8001').replace(/\/$/, '')}/api`;

let _ratesCache = null;
let _ratesCacheTs = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const ZERO_DEC = new Set(['XOF', 'XAF', 'NGN', 'GHS']);

// Une même conversion n'est demandée qu'une fois : plusieurs composants d'une page la demandent au même instant
// (sélecteur de devise, brouillon de proposition, séquestre), et une rafale de requêtes identiques en perdait.
const CONVERT_TTL = 60 * 1000;
const _conversions = new Map();

const CurrencyService = {

  /** Récupère les taux de change actuels (base EUR). Résultat mis en cache 5 min. */
  async getRates() {
    const now = Date.now();
    if (_ratesCache && now - _ratesCacheTs < CACHE_TTL) return _ratesCache;
    try {
      const { data } = await axios.get(`${API}/currencies/rates`);
      _ratesCache = data;
      _ratesCacheTs = now;
      return data;
    } catch {
      return null;
    }
  },

  /**
   * Convertit `amount` de `fromCurrency` vers `toCurrency`.
   * Retourne un objet { amount, currency, convertedAmount, convertedCurrency,
   *                     exchangeRate, exchangeRateDate, exchangeProvider }
   * ou null en cas d'erreur.
   */
  async convert(amount, fromCurrency, toCurrency) {
    if (!amount || !fromCurrency || !toCurrency || fromCurrency === toCurrency) {
      return {
        amount,
        currency: fromCurrency,
        convertedAmount: amount,
        convertedCurrency: toCurrency,
        exchangeRate: 1.0,
        exchangeRateDate: null,
        exchangeProvider: 'identity',
      };
    }
    const key = `${amount}|${fromCurrency}|${toCurrency}`;
    const known = _conversions.get(key);
    if (known && Date.now() - known.ts < CONVERT_TTL) return known.promise;
    const promise = axios.post(`${API}/currencies/convert`, {
      amount,
      from_currency: fromCurrency,
      to_currency: toCurrency,
    }).then(({ data }) => data).catch(() => {
      _conversions.delete(key); // un échec n'est pas gardé : la demande suivante réessaie
      return null;
    });
    _conversions.set(key, { promise, ts: Date.now() });
    return promise;
  },

  /**
   * Formate un montant dans une devise donnée pour affichage.
   * Exemple : formatAmount(1234.5, 'EUR') → "1 234,5 €"
   *           formatAmount(60000, 'XOF') → "60 000 FCFA"
   */
  formatAmount(amount, currency) {
    if (amount == null || !currency) return '';
    const sym = ccySym(currency);
    const decimals = ZERO_DEC.has(currency) ? 0 : 2;
    const n = Number(amount).toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    });
    return `${n} ${sym}`;
  },
};

export default CurrencyService;
